import * as React from "react";
import { useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { ArrowRight, Loader2 } from "lucide-react";

import { aiFillAction, type ManualActionResponse } from "@ando/core";
import { AndoLogo, cn, toast } from "@ando/ui";
import type { ActionFieldValue } from "@ando/ui";

import { proposedFieldsFromManualResponse } from "../utils/manualActionFields";

/**
 * ActionAiFillBar — the "Compila con AI" affordance on every action form.
 *
 * Mirrors the email composer's promise: you can fill the form BY HAND, but you
 * always have a one-shot AI assist to draft it for you from a natural-language
 * instruction (``feedback_manual_action_forms_need_ai_assist.md``). Per-field
 * AI rewriting is already covered by ``<InlineAIRefiner>`` on free-text
 * fields; this bar is the WHOLE-FORM counterpart.
 *
 * Wiring: ``POST /actions/{id}/ai-fill`` runs the BE Vertex pipeline to
 * populate the proposal's fields (additive — never clobbers what the operator
 * already typed; high-risk action-time fields stay blank), then we hand the
 * fresh ``{field: value}`` map back to the host which patches the action store
 * → ``<ActionDrawer>`` re-seeds its values automatically (its ``proposedFields``
 * effect). No bespoke re-render path.
 */

export interface ActionAiFillBarProps {
  /** Proposal id to fill. */
  readonly proposalId: string;
  /** Suppress while the action is executing / read-only. */
  readonly disabled?: boolean;
  /** Receives the AI-populated field map so the host can patch the store. */
  readonly onFilled: (fields: Record<string, ActionFieldValue>) => void;
  /** Optional contextual placeholder (e.g. "Descrivi la fattura…"). */
  readonly placeholder?: string;
  readonly className?: string;
}

export const ActionAiFillBar: React.FC<ActionAiFillBarProps> = ({
  proposalId,
  disabled,
  onFilled,
  placeholder,
  className,
}) => {
  const { t } = useLingui();
  const [instruction, setInstruction] = React.useState("");

  const fill = useMutation<ManualActionResponse, Error, string>({
    mutationFn: (text) => aiFillAction(proposalId, { instruction: text }),
    onSuccess: (res) => {
      onFilled(proposedFieldsFromManualResponse(res));
      setInstruction("");
      toast.success(t`Campi compilati con l'AI — controllali prima di confermare`);
    },
    onError: (err) => {
      toast.error(err.message ?? t`Impossibile compilare con l'AI. Riprova.`);
    },
  });

  const busy = fill.isPending;
  const canSubmit = instruction.trim().length > 0 && !busy && !disabled;

  return (
    <form
      aria-busy={busy}
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) fill.mutate(instruction.trim());
      }}
      className={cn(
        "flex items-center gap-2 rounded-lg border border-accent/30 bg-accent/[0.04] px-2.5 py-2",
        className,
      )}
    >
      <AndoLogo size={16} aria-hidden />
      <input
        type="text"
        value={instruction}
        disabled={busy || disabled}
        onChange={(e) => setInstruction(e.target.value)}
        aria-label={t`Compila il modulo con l'AI: descrivi cosa vuoi`}
        placeholder={placeholder ?? t`Compila con l'AI: descrivi cosa vuoi…`}
        className={cn(
          "min-w-0 flex-1 bg-transparent text-ink-1 text-sm placeholder:text-ink-3",
          "focus:outline-none disabled:opacity-50",
        )}
      />
      <button
        type="submit"
        disabled={!canSubmit}
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 rounded-md bg-accent px-3 py-1.5",
          "font-medium text-[12.5px] text-white transition-colors hover:bg-accent/90",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        )}
        {busy ? t`Compilo…` : t`Compila con AI`}
      </button>
    </form>
  );
};
ActionAiFillBar.displayName = "ActionAiFillBar";
